import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Grid, Header, List, Segment, SemanticICONS } from 'semantic-ui-react';
import Ceteicean from '../components/Ceteicean';
import { ResourceData, ResourcesContext } from '../providers/ResourcesProvider';
import { IIIFViewer } from '@performant-software/shared-components';
import ResourceHistory from '../components/ResourceHistory';
import { useTranslation } from 'react-i18next';

interface Props {
  resourceId: string;
}

const styles = {
  listItem: {
    marginBottom: '12px',
  },
};

const ResourceDetail: React.FC<Props> = (props) => {
  const [prevId, setPrevId] = useState<string | null>(null);

  const { resources, content, onGetContent } = useContext(ResourcesContext);

  const { t } = useTranslation();

  const resource = useMemo(
    () => resources.find((r) => r.localId === props.resourceId),
    [props.resourceId, resources]
  );

  useEffect(() => {
    if (resource && (!content || resource.guid !== prevId)) {
      onGetContent(resource.guid);
      setPrevId(resource.guid);
    }
  }, [content, onGetContent, prevId, resource]);

  const displayContent = () => {
    if (resource?.resourceType === 'facs') {
      return (
        <IIIFViewer
          manifestId={`/public/resources/${resource.guid}/manifest`}
        />
      );
    } else if (content) {
      return <Ceteicean content={content} />;
    }
  };

  const buildListItem = (field: keyof ResourceData, icon: SemanticICONS) => (
    <List.Item style={styles.listItem}>
      <List.Icon name={icon} />
      <List.Content>
        <>
          <Header as="h6">{t(`resources.${field}`)}</Header>
          {(resource as ResourceData)[field] || t('general.na')}
        </>
      </List.Content>
    </List.Item>
  );

  return (
    <Grid columns={2}>
      <Grid.Column width={4}>
        <Segment>
          <Header as="h3">{t('resources.history')}</Header>
          {resource ? <ResourceHistory resource={resource} /> : null}
        </Segment>
        <Segment>
          <Header as="h3">{t('resources.metadata')}</Header>
          <List>
            {buildListItem('name', 'file text')}
            {buildListItem('localId', 'id badge')}
            {buildListItem('resourceType', 'cog')}
          </List>
        </Segment>
      </Grid.Column>
      <Grid.Column width={12}>
        <Segment>
          <Header as="h2">{t('resources.content')}</Header>
          <Segment>{displayContent()}</Segment>
        </Segment>
      </Grid.Column>
    </Grid>
  );
};

export default ResourceDetail;
